import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

import type { ApiStatus } from '@nf/types/api';
import type { ReservationCreationResult } from '@/types/booked-bet';
import { AcceptTicketStatus } from '@/hooks/query';
import { useBetslipStore } from '@/store/betslip';
import { useAuthActions } from '@/store/auth';
import { Maybe, identity, matchEquals } from '@nf/utils-common/fpw/monadic';
import { idealRoundFixed } from '@nf/utils-common/numbers';
import { addCommasWithFix, getDisplayOdds } from '@/utils/odds-utils';
import { reservationService } from '../../services/reservation.service';

import { BetTicketOddsGroup } from '@/features/betslip/betslip-container/components/bet-ticket-odds-group';
import { TicketChoice, SportBetTypeName, Score, TeamName } from '@/features/betslip/betslip-container/components/bet-ticket';
import { BookingConfirmHeader } from '../booking-confirm-header';
import { BookingInfoParagraph } from '../booking-info-paragraph';
import { useCompletePromoteBookedBet } from './use-complete-promote-booked-bet';

export const BookingConfirmContainer = () => {
	const t = useTranslations('General');
	const [reservationResult, setReservationResult] = useState<ReservationCreationResult | null>(null);
	const [apiStatus, setApiStatus] = useState<ApiStatus>('loading');
	const [errorInfo, setErrorInfo] = useState('');
	const completePromoteBookedBet = useCompletePromoteBookedBet();

	const { updateAuthToken } = useAuthActions();
	
	const [
		currentMultiTickets,
		bookingData,
	] = useBetslipStore(state => [
		state.currentMultiTickets,
		state.bookingData,
	]);
	const ticket = currentMultiTickets[0];

	useEffect(() => {
		if (!ticket || !bookingData) return;

		// Creates a resercation by calling the API
		const apiPayload = {
			LeagueId: ticket.LeagueId,
			MatchId: ticket.Matchid,
			SportType: ticket.SportType,
			BetType: Number(ticket.Bettype),
			HomeTeamId: ticket.HomeId,
			AwayTeamId: ticket.AwayId,
			OddsRanges: {
				Lower: bookingData.expectedMinOdds,
				Upper: bookingData.expectedMaxOdds
			},
			BetTeam: ticket.Betteam,
			TeamScore: {
				Home: ticket.LiveHomeScore,
				Away: ticket.LiveAwayScore
			},
			Hdp: Number(ticket.Line),
			Stake: ticket.stake,
			IsLive: ticket.IsLive,
			SrcOddsInfo: ticket.SrcOddsInfo,
		};

		const subscription = reservationService
			.createReservation(apiPayload)
			.subscribe({
				next: async (result) => {
					const data = await result.data;

					// Update the user's token
					Maybe(result.jwtToken).fold(
						identity,
						(jwtToken: string) => {
							updateAuthToken(jwtToken);
						}
					);
				
					if (data.Success && data.Data) {
						setReservationResult(data.Data);
					
						if (data.Data.ErrorCode === 0) {
							setApiStatus('success');
						} else {
							setApiStatus('error');
							setErrorInfo(data.Data.LicenseeErrorMessage)
						}
					} else {
						setApiStatus('error');
						setErrorInfo(`${data.Code}: ${data.Message}`);
					}
					await completePromoteBookedBet();
				},
				error: err => {
					setApiStatus('error');
					setErrorInfo(err instanceof Error ? err.message : 'Create reservation error');
					console.error(err);
				}
			});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	if (!ticket || !bookingData) return null;

	const oddsRange = [bookingData.expectedMinOdds, bookingData.expectedMaxOdds];
	const payoutRange = reservationService.calculatePayoutRange({
		oddsRange,
		bookingOddsType: bookingData.bookingOddsType,
		ticketStake: ticket.stake,
		ticketPriceType: ticket.PriceType,
	});
	const formattedOdds = (odds: number) => 
		reservationService.formatBookingValue(odds, bookingData.bookingOddsType, ticket.PriceType, Number(ticket.Bettype));

	const uiStatus: AcceptTicketStatus = matchEquals(apiStatus)
		.on('loading', () => 'waiting')
		.on('success', () => 'running')
		.on('error', () => 'reject')
		.otherwise(() => 'waiting');

	return (
		<>
			<BookingConfirmHeader 
				status={uiStatus}
				ticket={ticket} 
			/>

			<div className="ticket">
				<div className="ticket__wrap">
					<div className="ticket__status"></div>
					<div className="ticket__container">
						<div className="ticket__header">
							<TicketChoice ticket={ticket} />
						</div>
						<div className="ticket__row">
							<SportBetTypeName ticket={ticket} />
							<Score ticket={ticket} />
						</div>
						<TeamName ticket={ticket} />

						{errorInfo ? (
							<div className="ticket__row">
								<span className="text-error">{errorInfo}</span>
							</div>
						) : null}
					</div>
					<BetTicketOddsGroup 
						ticket={ticket}
						isParlayConfirmBet={false}
					/>
				</div>
			</div>
			
			<div className="betting-info">
				{reservationResult?.Data ? (
					<div className="ticket-id">{t('lbl_ECPBookingID')} : {reservationResult.Data}</div>
				) : null}

				<BookingInfoParagraph title={t('lbl_ECPOddsRange')}>
					{oddsRange
						.map(formattedOdds)
						.join(' ~ ')
					}
				</BookingInfoParagraph>
				<BookingInfoParagraph title={t('lbl_stake')}>
					{idealRoundFixed(ticket.stake, 2)}
				</BookingInfoParagraph>
				<BookingInfoParagraph title={t('lbl_payout')}>
					{payoutRange
						.map(payout => addCommasWithFix(payout, 2))
						.join(' ~ ')
					}
				</BookingInfoParagraph>
			</div>
		</>
	);
};